import React, { useMemo, useState } from 'react';
import type { RadioButtonListElement } from 'models/forms/formElements/RadioButtonListElement';

type Props = {
  element: RadioButtonListElement;
  isEditing: boolean;
  onUpdate: (updatedElement: RadioButtonListElement) => void;
  isDisabled?: boolean;
};

const parseOptions = (text: string): string[] => {
  return text
    .split(/\r?\n|,/g)
    .map((o) => o.trim())
    .filter(Boolean);
};

const RadioButtonListElementComponent: React.FC<Props> = ({ element, isEditing, onUpdate, isDisabled }) => {
  const [name, setName] = useState(element.name);
  const [optionsText, setOptionsText] = useState((element.options || []).join('\n'));

  const options = useMemo(() => element.options || [], [element.options]);

  const updateOptions = (text: string) => {
    setOptionsText(text);
    onUpdate({
      ...element,
      options: parseOptions(text),
    });
  };

  if (isEditing) {
    return (
      <div className="space-y-3">
        <div>
          <p className="mb-1 text-sm text-gray-700">Label</p>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              const next = e.target.value;
              setName(next);
              onUpdate({ ...element, name: next });
            }}
            className="block w-full px-3 py-2 border border-gray-300 rounded"
          />
        </div>

        <div>
          <p className="mb-1 text-sm text-gray-700">Options (comma or newline separated)</p>
          <textarea
            value={optionsText}
            onChange={(e) => updateOptions(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded min-h-[90px]"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="font-medium text-gray-700">{element.name}{element.isRequired ? ' *' : ''}</div>
      <div className="space-y-1">
        {options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name={element.id}
              checked={(element.value || '') === opt}
              onChange={() => onUpdate({ ...element, value: opt })}
              disabled={isDisabled ?? false}
            />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default RadioButtonListElementComponent;
